import React, { useEffect, useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { Divider } from '@mui/material';
import CelebrationIcon from '@mui/icons-material/Celebration';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EventIcon from '@mui/icons-material/Event';
import PlaceIcon from '@mui/icons-material/Place';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { verifyScan } from '../../services/anandaRewardsApi';
import './ScanVerification.css';

const PAGE_SIZE = 5;

function formatDateTime(value) {
  if (!value) return { date: '-', time: '-' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: value, time: '' };
  return {
    date: d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
    time: d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
  };
}

/**
 * Dynamic, API-driven scan verification block.
 * Renders a code search box (optional), the verification result, and the
 * scan history for the resolved code. Every value comes from the Ananda
 * Rewards API response — nothing here is hardcoded.
 */
function ScanVerification({ initialCode = '', autoVerify = true, allowSearch = true, onResult }) {
  const [inputCode, setInputCode] = useState(initialCode);
  const [status, setStatus] = useState('idle'); // idle | loading | success | empty | error
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [historyQuery, setHistoryQuery] = useState('');
  const [historyPage, setHistoryPage] = useState(1);

  const runVerification = async (code) => {
    setStatus('loading');
    setErrorMessage('');
    const res = await verifyScan(code);

    if (!res.ok) {
      setResult(null);
      setErrorMessage(res.message);
      setStatus(res.kind === 'validation' ? 'validation' : res.kind === 'network' ? 'network' : 'error');
      if (onResult) onResult(null, res.kind);
      return;
    }

    setResult(res.data);
    setStatus('success');
    setHistoryPage(1);
    if (onResult) onResult(res.data, 'success');
  };

  useEffect(() => {
    setInputCode(initialCode);
    if (autoVerify && initialCode) {
      runVerification(initialCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCode]);

  const handleVerify = (e) => {
    if (e) e.preventDefault();
    runVerification(inputCode);
  };

  const handleClear = () => {
    setInputCode('');
    setResult(null);
    setStatus('idle');
    setErrorMessage('');
  };

  const filteredHistory = useMemo(() => {
    const history = result?.history || [];
    if (!historyQuery.trim()) return history;
    const q = historyQuery.trim().toLowerCase();
    return history.filter((row) => {
      const { date } = formatDateTime(row.scannedAt);
      return (row.ip || '').toLowerCase().includes(q) || date.toLowerCase().includes(q);
    });
  }, [result, historyQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredHistory.length / PAGE_SIZE));
  const pagedHistory = filteredHistory.slice((historyPage - 1) * PAGE_SIZE, historyPage * PAGE_SIZE);

  return (
    <div className="scan-verify">
      {allowSearch && (
        <Card className="scan-verify-search-card">
          <h3 className="scan-verify-title">Verify QR / Product Code</h3>
          <form onSubmit={handleVerify} className="scan-verify-search-form">
            <TextField
              className="scan-verify-input"
              variant="outlined"
              size="small"
              placeholder="Enter or scan code"
              value={inputCode}
              onChange={(e) => setInputCode(e.target.value)}
            />
            <div className="scan-verify-search-actions">
              <Button
                variant="contained"
                type="submit"
                startIcon={<SearchIcon />}
                disabled={status === 'loading'}
              >
                {status === 'loading' ? 'Verifying…' : 'Verify Code'}
              </Button>
              <Button variant="outlined" type="button" startIcon={<ClearIcon />} onClick={handleClear}>
                Clear
              </Button>
            </div>
          </form>
        </Card>
      )}

      {status === 'loading' && (
        <Card className="scan-verify-state-card">
          <div className="scan-verify-spinner" aria-label="Loading" />
          <p>Verifying code…</p>
        </Card>
      )}

      {status === 'validation' && (
        <Card className="scan-verify-state-card scan-verify-state-warn">
          <ReportProblemIcon />
          <p>{errorMessage || 'Please enter a valid QR/product code.'}</p>
        </Card>
      )}

      {status === 'network' && (
        <Card className="scan-verify-state-card scan-verify-state-error">
          <ReportProblemIcon />
          <p>Unable to connect to the server. Please try again.</p>
        </Card>
      )}

      {status === 'error' && (
        <Card className="scan-verify-state-card scan-verify-state-error">
          <ReportProblemIcon />
          <p>{errorMessage || 'No scan information found.'}</p>
        </Card>
      )}

      {status === 'success' && result && (
        <div className="scan-result-layout">
          <Card className="scan-verify-result-card">
            <div className="scan-status-badge status-genuine">
              <CelebrationIcon />
              Genuine Product
            </div>
            <Divider />
            <Grid container spacing={2} className="scan-result-grid">
              {result.productImageUrl && (
                <Grid item xs={12} md={4} className="scan-result-image-col">
                  <img src={result.productImageUrl} alt={result.productName || 'Product'} className="scan-result-image" />
                </Grid>
              )}
              <Grid item xs={12} md={result.productImageUrl ? 8 : 12}>
                <table className="scan-result-table">
                  <tbody>
                    {result.productName && (
                      <tr>
                        <th>Product</th>
                        <td>{result.productName}</td>
                      </tr>
                    )}
                    {result.code && (
                      <tr>
                        <th>Code</th>
                        <td>{result.code}</td>
                      </tr>
                    )}
                    {result.categoryName && (
                      <tr>
                        <th>Category</th>
                        <td>{result.categoryName}</td>
                      </tr>
                    )}
                    {result.businessName && (
                      <tr>
                        <th>Business</th>
                        <td>{result.businessName}</td>
                      </tr>
                    )}
                    <tr>
                      <th>Scan Count</th>
                      <td>{result.scanCount}</td>
                    </tr>
                    {result.lastScannedAt && (
                      <tr>
                        <th>Last Scanned</th>
                        <td>
                          {formatDateTime(result.lastScannedAt).date} {formatDateTime(result.lastScannedAt).time}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {result.message && <p className="scan-result-message">{result.message}</p>}
              </Grid>
            </Grid>
          </Card>

          {result.history && result.history.length > 0 && (
            <Accordion className="scan-history-card" defaultExpanded disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="scan-history-summary">
                <div className="scantitle">
                  <HistoryIcon fontSize="small" /> Scan History
                  <span className="scan-history-count">{result.history.length}</span>
                </div>
              </AccordionSummary>
              <AccordionDetails className="scan-history-details">
              {result.history.length > PAGE_SIZE && (
                  <TextField
                    className="scan-history-filter"
                    variant="outlined"
                    size="small"
                    placeholder="Search by date or IP"
                    value={historyQuery}
                    onChange={(e) => {
                      setHistoryQuery(e.target.value);
                      setHistoryPage(1);
                    }}
                  />
                )}

              {filteredHistory.length === 0 ? (
                <p className="scan-history-empty">No matching scan records found.</p>
              ) : (
                <>
                  <table className="scanhistorytable css-serial scan-history-table-desktop">
                    <thead>
                      <tr>
                        <th>Sr.</th>
                        <th>Scan Date</th>
                        <th>Scan Time</th>
                        <th>IP Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedHistory.map((row, i) => {
                        const { date, time } = formatDateTime(row.scannedAt);
                        return (
                          <tr key={row.id || i}>
                            <td>{(historyPage - 1) * PAGE_SIZE + i + 1}</td>
                            <td>{date}</td>
                            <td>{time}</td>
                            <td>{row.ip || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div className="scan-history-cards-mobile">
                    {pagedHistory.map((row, i) => {
                      const { date, time } = formatDateTime(row.scannedAt);
                      return (
                        <div className="scan-history-card-item" key={row.id || i}>
                          <div className="scan-history-card-row">
                            <EventIcon fontSize="small" /> {date} {time}
                          </div>
                          <div className="scan-history-card-row">
                            <PlaceIcon fontSize="small" /> {row.ip || '-'}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {totalPages > 1 && (
                    <div className="scan-history-pagination">
                      <Button
                        size="small"
                        disabled={historyPage <= 1}
                        onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <span>
                        Page {historyPage} of {totalPages}
                      </span>
                      <Button
                        size="small"
                        disabled={historyPage >= totalPages}
                        onClick={() => setHistoryPage((p) => Math.min(totalPages, p + 1))}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
              </AccordionDetails>
            </Accordion>
          )}
        </div>
      )}
    </div>
  );
}

export default ScanVerification;
